import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { CheckCircle, XCircle, Clock, Eye, MapPin, Calendar, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface PendingListing {
  id: string;
  title: string;
  address: string;
  price_egp: number;
  deposit_egp: number;
  unit_type: string;
  furnished: boolean;
  approval_status: string;
  created_at: string;
  owner_id: string;
  photos: string[];
  profiles: {
    full_name: string;
    email: string;
  } | null;
  universities: {
    name_en: string;
    name_ar: string;
    city: string;
  } | null;
}

const ListingsApproval = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [pendingListings, setPendingListings] = useState<PendingListing[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingListings();
  }, []);

  const fetchPendingListings = async () => {
    try {
      setLoading(true);
      
      // Fetch pending listings
      const { data: listingsData, error: listingsError } = await supabase
        .from('listings')
        .select(`
          *,
          universities (
            name_en,
            name_ar,
            city
          )
        `)
        .eq('approval_status', 'pending')
        .order('created_at', { ascending: false });

      if (listingsError) {
        console.error('Error fetching pending listings:', listingsError);
        toast.error('Failed to load pending listings');
        return;
      }

      // Get unique owner IDs
      const ownerIds = [...new Set(listingsData?.map(listing => listing.owner_id) || [])];
      
      // Fetch profiles for these owners
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', ownerIds);

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      // Create a map of profiles by ID for quick lookup
      const profilesMap = new Map();
      profilesData?.forEach(profile => {
        profilesMap.set(profile.id, profile);
      });

      // Transform the data
      const transformedListings = (listingsData || []).map(listing => ({
        ...listing,
        profiles: profilesMap.get(listing.owner_id) || null,
        universities: listing.universities as { name_en: string; name_ar: string; city: string } | null
      }));

      setPendingListings(transformedListings);
    } catch (error) {
      console.error('Exception fetching pending listings:', error);
      toast.error('Failed to load pending listings');
    } finally {
      setLoading(false);
    }
  };

  const createNotification = async (userId: string, title: string, message: string, type: string) => {
    console.log('Creating notification for user:', userId, 'Type:', type, 'Title:', title);
    
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert({
          user_id: userId,
          title,
          message,
          type,
          read: false
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating notification:', error);
        throw error;
      }

      console.log('Notification created successfully:', data);
      return data;
    } catch (error) {
      console.error('Exception creating notification:', error);
      throw error;
    }
  };

  const handleApproval = async (listingId: string, status: 'approved' | 'rejected') => {
    if (!user) {
      console.error('No user found for approval action');
      return;
    }

    console.log('Starting approval process for listing:', listingId, 'Status:', status, 'Admin:', user.id);

    try {
      const updateData: any = {
        approval_status: status,
        approved_by: user.id,
        approved_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('listings')
        .update(updateData)
        .eq('id', listingId);

      if (error) {
        console.error('Error updating listing approval:', error);
        toast.error('Failed to update listing approval');
        return;
      }

      console.log('Listing status updated successfully');

      // Find the listing to get the owner ID and title
      const listing = pendingListings.find(l => l.id === listingId);
      
      if (listing) {
        console.log('Found listing for notification:', listing.title, 'Owner:', listing.owner_id);
        
        try {
          if (status === 'approved') {
            // Create notification for approved listing
            await createNotification(
              listing.owner_id,
              'Property Listing Approved! 🎉',
              `Your property listing "${listing.title}" has been approved and is now live.`,
              'listing_approved'
            );
            toast.success('Listing approved and notification sent successfully');
          } else if (status === 'rejected') {
            // Create notification for rejected listing
            await createNotification(
              listing.owner_id,
              'Property Listing Rejected ❌',
              `Your property listing "${listing.title}" has been rejected. Please review the listing details and resubmit with the necessary corrections.`,
              'general'
            );
            toast.success('Listing rejected and notification sent successfully');
          }
        } catch (notificationError) {
          console.error('Failed to create notification:', notificationError);
          toast.error('Listing updated but failed to send notification');
        }
      } else {
        console.error('Could not find listing for notification');
        toast.error('Listing updated but could not send notification');
      }

      fetchPendingListings(); // Refresh the list
    } catch (error) {
      console.error('Exception updating listing approval:', error);
      toast.error('Failed to update listing approval');
    }
  };

  const getUnitTypeLabel = (type: string) => {
    const labels = {
      room: { en: 'Private Room', ar: 'غرفة خاصة' },
      shared: { en: 'Shared Room', ar: 'غرفة مشتركة' },
      entire: { en: 'Entire Unit', ar: 'وحدة كاملة' }
    };
    return labels[type as keyof typeof labels]?.[language === 'ar' ? 'ar' : 'en'] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          {t('pending_listings_approval', 'الموافقة على الإعلانات المعلقة', 'Pending Listings Approval')}
          <Badge variant="destructive" className="ml-2">
            {pendingListings.length}
          </Badge>
        </CardTitle>
        <CardDescription>
          {t('review_approve_listings', 'راجع واقبل الإعلانات الجديدة', 'Review and approve new property listings')}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {pendingListings.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('no_pending_listings', 'لا توجد إعلانات معلقة', 'No pending listings to review')}</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('property', 'العقار', 'Property')}</TableHead>
                  <TableHead>{t('owner', 'المالك', 'Owner')}</TableHead>
                  <TableHead>{t('location', 'الموقع', 'Location')}</TableHead>
                  <TableHead>{t('price', 'السعر', 'Price')}</TableHead>
                  <TableHead>{t('type', 'النوع', 'Type')}</TableHead>
                  <TableHead>{t('submitted', 'تاريخ التقديم', 'Submitted')}</TableHead>
                  <TableHead>{t('actions', 'الإجراءات', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingListings.map((listing) => (
                  <TableRow key={listing.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {listing.photos?.[0] && (
                          <img 
                            src={listing.photos[0]} 
                            alt={listing.title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <div className="font-medium">{listing.title}</div>
                          <div className="text-sm text-muted-foreground">{listing.address}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{listing.profiles?.full_name || 'N/A'}</div>
                        <div className="text-sm text-muted-foreground">{listing.profiles?.email || 'N/A'}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{listing.universities?.city || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <DollarSign className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{listing.price_egp.toLocaleString()} EGP</span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {t('deposit', 'تأمين', 'Deposit')}: {listing.deposit_egp.toLocaleString()} EGP
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getUnitTypeLabel(listing.unit_type)}
                      </Badge>
                      {listing.furnished && (
                        <Badge variant="secondary" className="ml-1 text-xs">
                          {t('furnished', 'مفروش', 'Furnished')}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {new Date(listing.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApproval(listing.id, 'approved')}
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('approve', 'موافقة', 'Approve')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApproval(listing.id, 'rejected')}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <XCircle className="h-4 w-4 mr-1" />
                          {t('reject', 'رفض', 'Reject')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ListingsApproval;
